import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { engagedMembersService, MyEngagementStats } from '../services/engagedMembersService';
import { publicQuestionsService, PublicQuestionList } from '../services/publicQuestionsService';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

const SECTION_DETAILS: Record<'VCN' | 'HDR' | 'SANTE', { label: string; icon: string; badgeClass: string }> = {
  VCN: { label: 'VCN (Université)', icon: '🎓', badgeClass: 'bg-blue-100 text-blue-800' },
  HDR: { label: 'HDR', icon: '🏛️', badgeClass: 'bg-purple-100 text-purple-800' },
  SANTE: { label: 'SANTÉ', icon: '🩺', badgeClass: 'bg-emerald-100 text-emerald-800' },
};

export const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    section: 'VCN' as 'VCN' | 'HDR' | 'SANTE',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [engagementStats, setEngagementStats] = useState<MyEngagementStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [myQuestions, setMyQuestions] = useState<PublicQuestionList[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        section: (user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN',
      });
      fetchEngagementStats();
      fetchMyQuestions();
    }
  }, [user]);

  const fetchMyQuestions = async () => {
    if (!user) return;
    try {
      setLoadingQuestions(true);
      const data = await publicQuestionsService.getMyQuestions();
      setMyQuestions(data.questions);
    } catch (error) {
      console.error('Erreur lors du chargement de vos questions:', error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const fetchEngagementStats = async () => {
    if (!user) return;
    try {
      setLoadingStats(true);
      const stats = await engagedMembersService.getMyEngagementStats();
      setEngagementStats(stats);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques d\'engagement:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-8xl mb-6">🔒</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Non connecté</h2>
          <p className="text-gray-600">Veuillez vous connecter pour accéder à votre profil</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    try {
      await updateUser(formData);
      setEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-white via-green-50/30 to-green-100/50 overflow-hidden">
      {/* Motifs décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-white/90 backdrop-blur-sm rounded-2xl px-8 py-5 shadow-xl border border-green-100/50">
            <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-green-700 via-green-600 to-green-800 bg-clip-text text-transparent mb-4">
              👤 Mon Profil
            </h1>
            <p className="text-gray-600 text-lg">Gérez vos informations personnelles</p>
          </div>
        </div>

        {/* Card principale */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-green-100/50 p-8 sm:p-10">
          {success && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6 animate-slide-down">
              <p className="text-green-700 font-medium flex items-center space-x-2">
                <span>✅</span>
                <span>Profil mis à jour avec succès !</span>
              </p>
            </div>
          )}

          {!editing ? (
            <>
              {/* Avatar et infos principales */}
              <div className="text-center mb-8 pb-8 border-b border-gray-200">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-green-700 rounded-full text-white text-4xl font-bold mb-4 shadow-lg">
                  {(user.first_name || user.username || 'U')[0].toUpperCase()}
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {user.first_name && user.last_name 
                    ? `${user.first_name} ${user.last_name}` 
                    : user.username}
                </h2>
                <p className="text-gray-600">{user.email}</p>
                <span className={`inline-block mt-3 px-4 py-2 rounded-full text-sm font-semibold ${
                  user.role === 'admin' 
                    ? 'bg-purple-100 text-purple-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {user.role === 'admin' ? '👑 Administrateur' : '👤 Membre'}
                </span>
              {user.section && (
                <span
                  className={`inline-block mt-3 ml-2 px-4 py-2 rounded-full text-sm font-semibold ${
                    SECTION_DETAILS[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].badgeClass
                  }`}
                >
                  {SECTION_DETAILS[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].icon}{' '}
                  {SECTION_DETAILS[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].label}
                </span>
              )}
              </div>

              {/* Badge Top Member */}
              {engagementStats?.is_top_member && (
                <div className="mb-6 animate-pulse">
                  <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-xl p-6 border-4 border-yellow-300 shadow-2xl">
                    <div className="flex items-center justify-center space-x-4">
                      <div className="text-5xl">🏆</div>
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-white mb-2">⭐ Membre du Top ! ⭐</h3>
                        <p className="text-yellow-100 text-lg">
                          Félicitations ! Vous êtes le membre le plus engagé de la plateforme !
                        </p>
                        <p className="text-yellow-100 text-sm mt-2">
                          {engagementStats.stats?.total_score} interactions • {engagementStats.stats?.total_likes} ❤️ • {engagementStats.stats?.total_comments} 💬
                        </p>
                      </div>
                      <div className="text-5xl">🏆</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Statut dans le classement */}
              {engagementStats?.is_member && engagementStats.rank && engagementStats.rank > 1 && (
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-4 border border-blue-300 shadow-lg">
                    <div className="flex items-center justify-center space-x-3">
                      <span className="text-3xl">🎯</span>
                      <div className="text-center text-white">
                        <p className="font-bold text-lg">Votre classement : #{engagementStats.rank}</p>
                        <p className="text-sm text-blue-100">
                          Sur {engagementStats.total_members} membres actifs
                        </p>
                      </div>
                      <span className="text-3xl">🎯</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Statistiques d'engagement */}
              {engagementStats?.is_member && engagementStats.stats && (
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">📊 Mes Statistiques d'Engagement</h3>
                  
                  {/* Cartes de résumé */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
                      <div className="text-center">
                        <div className="text-3xl font-bold mb-1">{engagementStats.stats.total_score}</div>
                        <div className="text-green-100 text-sm">Score Total</div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
                      <div className="text-center">
                        <div className="text-3xl font-bold mb-1">{engagementStats.stats.total_likes}</div>
                        <div className="text-blue-100 text-sm">❤️ Likes</div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                      <div className="text-center">
                        <div className="text-3xl font-bold mb-1">{engagementStats.stats.total_comments}</div>
                        <div className="text-purple-100 text-sm">💬 Commentaires</div>
                      </div>
                    </div>
                  </div>

                  {/* Graphiques */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Likes par type */}
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h4 className="text-lg font-bold text-gray-800 mb-4">Likes par Type</h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Exposés', value: engagementStats.stats.likes_by_type.expose },
                              { name: 'Ramadan', value: engagementStats.stats.likes_by_type.ramadan },
                              { name: 'Événements', value: engagementStats.stats.likes_by_type.event },
                              { name: 'Audios', value: engagementStats.stats.likes_by_type.audio },
                              { name: 'Daara', value: engagementStats.stats.likes_by_type.daara },
                            ].filter(item => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry: any) => `${entry.name}: ${entry.value}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {[
                              { name: 'Exposés', value: engagementStats.stats.likes_by_type.expose },
                              { name: 'Ramadan', value: engagementStats.stats.likes_by_type.ramadan },
                              { name: 'Événements', value: engagementStats.stats.likes_by_type.event },
                              { name: 'Audios', value: engagementStats.stats.likes_by_type.audio },
                              { name: 'Daara', value: engagementStats.stats.likes_by_type.daara },
                            ].filter(item => item.value > 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Commentaires par type */}
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h4 className="text-lg font-bold text-gray-800 mb-4">Commentaires par Type</h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart
                          data={[
                            { name: 'Exposés', value: engagementStats.stats.comments_by_type.expose },
                            { name: 'Ramadan', value: engagementStats.stats.comments_by_type.ramadan },
                            { name: 'Événements', value: engagementStats.stats.comments_by_type.event },
                            { name: 'Daara', value: engagementStats.stats.comments_by_type.daara },
                          ].filter(item => item.value > 0)}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#8b5cf6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Détails par type */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Détails par Type de Contenu</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center">
                        <p className="text-2xl mb-2">📝</p>
                        <p className="text-sm text-gray-600 mb-1">Exposés</p>
                        <p className="font-bold text-blue-600">{engagementStats.stats.likes_by_type.expose} ❤️</p>
                        <p className="font-bold text-blue-600">{engagementStats.stats.comments_by_type.expose} 💬</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl mb-2">🌙</p>
                        <p className="text-sm text-gray-600 mb-1">Ramadan</p>
                        <p className="font-bold text-purple-600">{engagementStats.stats.likes_by_type.ramadan} ❤️</p>
                        <p className="font-bold text-purple-600">{engagementStats.stats.comments_by_type.ramadan} 💬</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl mb-2">📅</p>
                        <p className="text-sm text-gray-600 mb-1">Événements</p>
                        <p className="font-bold text-amber-600">{engagementStats.stats.likes_by_type.event} ❤️</p>
                        <p className="font-bold text-amber-600">{engagementStats.stats.comments_by_type.event} 💬</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl mb-2">🎵</p>
                        <p className="text-sm text-gray-600 mb-1">Audios</p>
                        <p className="font-bold text-indigo-600">{engagementStats.stats.likes_by_type.audio} ❤️</p>
                        <p className="font-bold text-indigo-600">- 💬</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl mb-2">📖</p>
                        <p className="text-sm text-gray-600 mb-1">Daara</p>
                        <p className="font-bold text-teal-600">{engagementStats.stats.likes_by_type.daara} ❤️</p>
                        <p className="font-bold text-teal-600">{engagementStats.stats.comments_by_type.daara} 💬</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Infos détaillées */}
              <div className="space-y-4 mb-8">
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">👤 Nom d'utilisateur</div>
                  <div className="font-bold text-gray-900 text-lg">{user.username}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">📧 Email</div>
                  <div className="font-bold text-gray-900 text-lg">{user.email}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">👤 Prénom</div>
                  <div className="font-bold text-gray-900 text-lg">{user.first_name || 'Non renseigné'}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">👤 Nom</div>
                  <div className="font-bold text-gray-900 text-lg">{user.last_name || 'Non renseigné'}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">📱 Téléphone</div>
                  <div className="font-bold text-gray-900 text-lg">{user.phone || 'Non renseigné'}</div>
                </div>
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <div className="text-sm text-gray-600 mb-1">🏛️ Section</div>
                <div className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <span>
                    {SECTION_DETAILS[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].icon}
                  </span>
                  <span>
                    {SECTION_DETAILS[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].label}
                  </span>
                </div>
              </div>
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">📅 Date d'inscription</div>
                  <div className="font-bold text-gray-900 text-lg">
                    {new Date(user.date_joined).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              </div>

              {/* Mes Questions */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
                    <span>❓</span>
                    <span>Mes Questions</span>
                  </h3>
                  <a
                    href="/faq"
                    className="text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    Voir toutes les questions →
                  </a>
                </div>

                {loadingQuestions ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  </div>
                ) : myQuestions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm mb-2">Vous n'avez pas encore posé de question</p>
                    <a
                      href="/faq"
                      className="inline-block mt-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                    >
                      Poser une question →
                    </a>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {myQuestions.map((question) => (
                      <div
                        key={question.id}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-green-300 transition-colors"
                      >
                        <p className="text-gray-800 font-medium text-sm mb-2 line-clamp-2">
                          {question.question}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {new Date(question.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                          {question.answer ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-semibold flex items-center space-x-1">
                              <span>✅</span>
                              <span>Répondu</span>
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-semibold flex items-center space-x-1">
                              <span>⏳</span>
                              <span>En attente</span>
                            </span>
                          )}
                        </div>
                        {question.answer && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-600 line-clamp-2">
                              {question.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setEditing(true)}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                ✏️ Modifier mon profil
              </button>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  👤 Prénom
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  placeholder="Votre prénom"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  👤 Nom
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  placeholder="Votre nom"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📱 Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  placeholder="+221 XX XXX XX XX"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  🏛️ Section *
                </label>
                <select
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value as 'VCN' | 'HDR' | 'SANTE' })}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  required
                >
                  <option value="VCN">🎓 VCN (Université)</option>
                  <option value="HDR">🏛️ HDR</option>
                  <option value="SANTE">🩺 SANTÉ</option>
                </select>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? 'Enregistrement...' : '✅ Enregistrer'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-4 rounded-xl transition-all duration-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
